import { useEffect, useState } from 'react';
import { Sun, Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning, Moon, CloudSun } from 'lucide-react';
import { useNiceWeather } from '../../hooks/useNiceWeather';

const ICONS = {
  clear: Sun,
  partly: CloudSun,
  cloudy: Cloud,
  fog: CloudFog,
  rain: CloudRain,
  snow: CloudSnow,
  storm: CloudLightning,
  night: Moon,
};

const formatNiceTime = (date) =>
  date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  });

const useNiceClock = () => {
  const [time, setTime] = useState(() => formatNiceTime(new Date()));

  useEffect(() => {
    const tick = () => setTime(formatNiceTime(new Date()));
    // Align to the next minute boundary so we update right when the minute flips
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
    let intervalId;
    const timeoutId = setTimeout(() => {
      tick();
      intervalId = setInterval(tick, 60_000);
    }, msUntilNextMinute);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  return time;
};

export const LogoStatus = ({ dark = false }) => {
  const time = useNiceClock();
  const weather = useNiceWeather();
  const Icon = weather ? ICONS[weather.atmosphere] || Sun : null;

  return (
    <div
      data-testid="logo-status"
      className={`mt-1 flex items-center gap-2 font-inter text-[11px] tracking-wide transition-colors ${
        dark ? 'text-slate-500' : 'text-white/75'
      }`}
    >
      <span className="tabular-nums font-medium">{time}</span>
      {weather && Icon && (
        <>
          <span className="w-[3px] h-[3px] rounded-full bg-current opacity-50" />
          <span className="flex items-center gap-1">
            <Icon className="w-3.5 h-3.5 text-amber-400" />
            <span className="tabular-nums">{weather.temp}°</span>
          </span>
        </>
      )}
    </div>
  );
};
